import _ from 'lodash';
// @mui
import { Stack } from '@mui/system';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
//components
import FormCreator from 'components/FormCreator';
import { fieldType, validationTypes, invalidFields, fieldsGroupType } from 'components/FormCreator/types';
//reducers
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getBeneficiaryById, getNewBeneficiary } from 'state/reducers/beneficiary.reducers';
import { useAppSelector } from 'hooks/redux.hooks';
import { getCountries } from 'state/reducers/global.reducers';
//sections
import { FormCard } from 'theme/elements/Forms';
import { FormHeader1 } from 'theme/elements/Headers';
//constants
import { Beneficiary, beneficiaryType } from 'constants/beneficiary.constants';
import { LATIN_OR_NUMBERS_REGEX } from 'constants/global.constants';
import { useMemo } from 'react';

export default function BeneficiaryDetailsForm(props: { handleChange: any; invalidFields: invalidFields }) {
	const t = useAppSelector(selectTranslations);
	const beneficiary: Beneficiary = useAppSelector(getNewBeneficiary);
	const prefixFields = `page.beneficiary.create.step.1.beneficiary_details.`;
	const Countries = useAppSelector(getCountries);
	const location = useLocation();
	const paths = location.pathname.split('/');
	const lastPath = paths[paths.length - 1];
	const found = useAppSelector(getBeneficiaryById(lastPath));

	const isCountryDisabled = useMemo(() => !!found, [found]);
	return (
		<Box component="form" noValidate>
			<FormCard>
				<FormHeader1 title={t(`${prefixFields}title`)} text={t(`${prefixFields}text`)} />
				<Stack spacing={2}>
					<FormCreator
						initialValidation={props.invalidFields}
						name="localPayment"
						handleChange={props.handleChange}
						fieldsGroups={[
							{
								type: fieldsGroupType.col,
								fields: [
									{
										type: fieldType.text,
										validation: [validationTypes.requierd],
										props: {
											required: true,
											disabled: found ? true : false,
											name: 'nickname',
											label: t(`${prefixFields}beneficiary_nickname.text`),
											placeholder: t(`${prefixFields}beneficiary_nickname.placeholder`),
											value: beneficiary.nickname,
											helperText: found
												? undefined
												: t(
														'page.beneficiary.create.step.1.beneficiary_details.beneficiary_nickname.helper_text'
												  ),
										},
									},
									{
										type: fieldType.radio,
										props: {
											required: true,
											name: 'type',
											label: t(`${prefixFields}beneficiary_type.text`),
											value: beneficiary.type,
											options: [
												{
													value: beneficiaryType.COMPANY,
													label: t(
														`${prefixFields}beneficiary_type.${_.lowerCase(
															beneficiaryType.COMPANY
														)}.text`
													),
												},
												/*{
                          value: beneficiaryType.INDIVIDUAL,
                          label: t(
                            `${prefixFields}beneficiary_type.${_.lowerCase(
                              beneficiaryType.INDIVIDUAL
                            )}.text`
                          ),
                        },*/
											],
										},
									},
								],
							},
						]}
					/>
					{_.isEqual(beneficiary.type, beneficiaryType.COMPANY) && (
						<Stack spacing={2}>
							<FormCreator
								initialValidation={props.invalidFields}
								name="localPayment"
								handleChange={props.handleChange}
								fieldsGroups={[
									{
										type: fieldsGroupType.col,
										fields: [
											{
												type: fieldType.text,
												validation: [validationTypes.requierd],
												props: {
													required: true,
													name: 'companyName',
													label: t(`${prefixFields}company_name.text`),
													placeholder: 'e.g. John Dow',
													value: beneficiary.companyName,
												},
											},
											{
												type: fieldType.text,
												validation: [validationTypes.requierd],
												props: {
													required: true,
													name: 'identificationNumber',
													label: t(`${prefixFields}company_number.text`),
													placeholder: t(`${prefixFields}company_number.placeholder`),
													value: beneficiary.identificationNumber,
												},
											},
											{
												type: fieldType.text,
												validation: [validationTypes.requierd],
												props: {
													required: true,
													name: 'address',
													label: t(`${prefixFields}address.text`),
													placeholder: t(`${prefixFields}address.placeholder`),
													value: beneficiary?.address,
												},
											},
											{
												type: fieldType.text,
												validation: [validationTypes.requierd, validationTypes.lettersOnly],
												props: {
													required: true,
													name: 'city',
													label: t(`${prefixFields}city.text`),
													placeholder: t(`${prefixFields}city.placeholder`),
													value: beneficiary?.city,
												},
											},
											{
												type: fieldType.select,
												validation: [validationTypes.requierd],
												valueProperty: 'id',
												labelProperty: 'name',
												props: {
													required: true,
													name: 'countryId',
													label: t(`${prefixFields}country.text`),
													placeholder: t(`${prefixFields}country.placeholder`),
													value: beneficiary.countryId || '',
													options: _.values(Countries),
													disabled: isCountryDisabled,
												},
											},
											{
												type: fieldType.text,
												validation: [validationTypes.website],
												props: {
													required: false,
													name: 'website',
													label: t(`${prefixFields}website.text`),
													placeholder: t(`${prefixFields}website.text`),
													value: beneficiary?.website,
												},
											},
										],
									},
								]}
							/>
						</Stack>
					)}
					{_.isEqual(beneficiary.type, beneficiaryType.INDIVIDUAL) && (
						<Stack spacing={2}>
							<FormCreator
								initialValidation={props.invalidFields}
								name="localPayment"
								handleChange={props.handleChange}
								fieldsGroups={[
									{
										type: fieldsGroupType.col,
										fields: [
											{
												type: fieldType.text,
												validation: [validationTypes.requierd],
												props: {
													required: true,
													name: 'firstName',
													label: t(`${prefixFields}first_name.text`),
													placeholder: t(`${prefixFields}first_name.placeholder`),
													value: beneficiary.firstName,
												},
											},
											{
												type: fieldType.text,
												validation: [validationTypes.requierd],
												props: {
													required: true,
													name: 'lastName',
													label: t(`${prefixFields}last_name.text`),
													placeholder: t(`${prefixFields}last_name.placeholder`),
													value: beneficiary.lastName,
												},
											},
											{
												type: fieldType.text,
												validation: [validationTypes.requierd],
												props: {
													type: 'number',
													required: true,
													name: 'identificationNumber',
													label: t(`${prefixFields}national_identification_number.text`),
													placeholder: t(
														`${prefixFields}national_identification_number.placeholder`
													),
													value: beneficiary.identificationNumber,
												},
											},
											{
												type: fieldType.text,
												validation: [validationTypes.requierd],
												props: {
													required: true,
													name: 'address',
													label: t(`${prefixFields}address.text`),
													placeholder: t(`${prefixFields}address.placeholder`),
													value: beneficiary?.address,
												},
											},
											{
												type: fieldType.text,
												validation: [validationTypes.requierd],
												props: {
													required: true,
													name: 'city',
													label: t(`${prefixFields}city.text`),
													placeholder: t(`${prefixFields}city.placeholder`),
													value: beneficiary?.city,
												},
											},
											{
												type: fieldType.select,
												validation: [validationTypes.requierd],
												valueProperty: 'id',
												labelProperty: 'name',
												props: {
													required: true,
													name: 'countryId',
													label: t(`${prefixFields}country.text`),
													placeholder: t(`${prefixFields}country.placeholder`),
													value: beneficiary.countryId || '',
													handleChange: props.handleChange,
													options: Countries,
													disabled: isCountryDisabled,
												},
											},
										],
									},
								]}
							/>
						</Stack>
					)}
				</Stack>
			</FormCard>
		</Box>
	);
}
